import { Box, styled, IconButton } from "@mui/material";
import logo from "../../assets/images";
import Button from "../button/button";
import Typography from "../typography/typography";
// import { useEffect, useState } from "react";
import { Document, Close } from "../icons";
import DocumentPreview from "../document-preview/document-preview";
import { enqueueSnackbar } from "notistack";
import { memo, useMemo, useEffect } from "react";
// import Avatar from "../avatar/avatar";

export interface UploadImageProps {
  type?: string;
  label: string;
  disabled?: boolean;
  error?: boolean;
  dataPosition?: number;
  dataName?: string;
  required?: boolean;
  max_size?: number;
  name: string;
  value: string;
  onChange: (e: any) => any;
  multiple: boolean;
  renderPreview?: (file: any) => React.ReactNode;
}
const UploadMedia = memo((props: UploadImageProps) => {
  const {
    type,
    label,
    disabled,
    error,
    dataName,
    dataPosition,
    required,
    max_size = 20,
    onChange,
    name,
    value,
    multiple,
    renderPreview,
    ...rest
  } = props;

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  // const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const StyledButton = styled(Button)(({ theme: { palette } }) => ({
    marginBottom: 8,
    "&.MuiButton-outlined": {
      color: palette.theme?.primary[1000],
    },
    "&.Mui-disabled": {
      background: palette.theme?.secondary[200],
      color: palette.theme?.secondary[800],
      borderWidth: "0.0625rem",
      borderStyle: "solid",
      borderColor: palette.theme?.secondary[400],
    },
  }));

  const allowedImageTypes = [
    "image/jpeg",  // JPG, JPEG
    "image/png",   // PNG
    "image/gif",   // GIF
    "image/bmp",   // BMP
    "image/webp",  // WebP
    "image/svg+xml", // SVG
    "image/tiff",  // TIFF
    "image/x-icon", // ICO (icon files)
    "image/heic",  // HEIC (High Efficiency Image Format)
    "image/heif"
  ]
  const allowedFileTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    'application/vnd.ms-powerpoint',
    'application/vnd.oasis.opendocument.text',
    'text/csv',
    'text/plain',
    'application/rtf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    "image/jpeg",  // JPG, JPEG
    "image/png",   // PNG
    "image/gif",   // GIF
    "image/bmp",   // BMP
    "image/webp",  // WebP
    "image/svg+xml", // SVG
    "image/tiff",  // TIFF
    "image/x-icon", // ICO (icon files)
    "image/heic",  // HEIC (High Efficiency Image Format)
    "image/heif"
  ];

  const isValidFileType = (file: any) => {
    if (type === "image") {
      return allowedImageTypes.includes(file.type);
    } else if (type === 'file') {
      return allowedFileTypes.includes(file.type);
    }
  };


  const handleUpload = (e: any) => {
    const files = [...e.target.files];
    const filterredFiles = files.map(file => {
      if (file && file.size <= max_size * 1024 * 1024) {
        if (isValidFileType(file)) {
          return file
        } else {
          enqueueSnackbar(`Invalid file type for ${file.name}. Please upload a valid document.`, { variant: 'error' })
          return null
        }
      }
    })?.filter(Boolean)
    onChange(multiple ? (value?.length ? [...value, ...filterredFiles] : filterredFiles) : filterredFiles[0]);
  };

  const handleRemoveFile = (index: number) => {
    if (Array.isArray(value)) {
      onChange(value.filter((_: any, i: number) => i !== index));
    } else {
      onChange(null);
    }
  };

  const getFileName = (file) => {
    if (!file) return ""
    if (file instanceof File) {
      return file.name
    } else if(typeof file === 'string') { 
      const parts = file?.split('/');
      return parts?.[parts?.length - 1];
    } else if(Array.isArray(file) && file?.length > 0) {
      return file.map(f => {
        const parts = f?.split('/');
        return parts?.[parts?.length - 1];
      }).join(', ')
    }
  }
  // const getImagePreview = (file) => {
  //   if (!file) return ""
  //   if (file instanceof File) {
  //     let previewUrl:any = ''
  //     const reader = new FileReader();
  //     reader.onload = () => {
  //       previewUrl = reader.result;
  //     };
  //     reader.readAsDataURL(file);

  //     return previewUrl
  //   } else {
  //     return file.includes(S3_BUCKET_URL) ? file : S3_BUCKET_URL + file
  //   }
  // }

  const DefaultPreview = ({ file }: { file: any }) => {
    const { fileUrl, fileName } = useMemo(() => {
      const isFile = file?.constructor?.name === 'File' || Object.prototype.toString.call(file) === '[object File]';
      return {
        fileUrl: isFile ? URL.createObjectURL(file) : file,
        fileName: isFile ? file.name : undefined,
      };
    }, [file]);

    useEffect(() => {
      return () => {
        if (fileUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(fileUrl);
        }
      };
    }, [fileUrl]);

    return <DocumentPreview fileUrl={fileUrl} fileName={fileName} />;
  };

  const renderDefaultPreview = (file: any) => <DefaultPreview file={file} />;

  const getSelectedFiles = (files: any) => {

    if (files) {
      if (Array.isArray(files)) {
        return files.map((file, index) => (
          <Box key={file instanceof File ? file.name : file} display="flex" gap={1} mb={1} alignItems='center'>
            {type === 'file' && <Document fontSize="small" />}
            <Typography
              type="s5"
              weight="medium"
              color={error ? "theme.error.600" : "theme.secondary.800"}
              data-position={dataPosition}
              data-name={dataName}
              noWrap
            >
              {getFileName(file)}
            </Typography>
            {(renderPreview || renderDefaultPreview)(file)}
            <IconButton size='small' onClick={() => handleRemoveFile(index)}>
              <Close fontSize='small' />
            </IconButton>
          </Box>
        ));
      }
      return (
        <Box display="flex" gap={1} mb={1} alignItems='center'>
          {type === 'file' && <Document fontSize="small" />}
          <Typography
            type="s5"
            weight="medium"
            color={error ? "theme.error.600" : "theme.secondary.800"}
            data-position={dataPosition}
            data-name={dataName}
            noWrap
          >
            {getFileName(files)}
          </Typography>
          {(renderPreview || renderDefaultPreview)(files)}
          <IconButton size='small' onClick={() => handleRemoveFile(0)}>
            <Close fontSize='small' />
          </IconButton>
        </Box>
      );
    }
  }

  // useEffect(() => {
  //   if(!multiple) {
  //     const parts = value?.split('/');
  //     // const documentPath = parts?.slice(3, parts.length - 1).join('/');
  //     const filename = parts?.[parts?.length - 1];

 
  //     if (value) setSelectedFile(filename)
  //   }
  // }, [multiple, value])

  return (
    <>
      <Typography
        type="s5"
        weight={"medium"}
        mb={0.5}
        color={error ? "theme.error.600" : "theme.secondary.800"}
        data-position={dataPosition}
        data-name={dataName}
      >
        {label} {required && " *"}
      </Typography>
      <div style={{ display: "flex" }}>
        <StyledButton
          component="label"
          startIcon={
            <img
              src={type === "image" ? logo.uploadImage : logo.uploadDocument}
            />
          }
          variant="outlined"
          disabled={disabled}
        >
          <Typography type='s5' weight="medium" color="theme.secondary.1000">Upload</Typography>
          <VisuallyHiddenInput
            type="file"
            accept={type === "file" ? allowedFileTypes.join(",") : (type === 'image' ? allowedImageTypes.join(",") : undefined)}
            // required={required}
            onChange={handleUpload}
            name={name}
            multiple={multiple}
            {...rest}
          />
        </StyledButton>
      </div >
      {getSelectedFiles(value)}

    </>
  );
})

export { UploadMedia };
export default UploadMedia;
